import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../api';
import { conEmpresa } from '../empresa';
import { useSede } from '../contexto/Sede';
import { usePeriodo } from '../contexto/Periodo';
import { useEsMovil, tablaAncha, filtro } from '../estilos/responsivo';
import { pesos } from './Tile';
import { CONTENEDOR_TABLA } from '../estilos/tarjeta';

const estadoColor = {
  borrador: { bg: '#f1f5f9', color: '#64748b' },
  emitida: { bg: '#dbeafe', color: '#2563eb' },
  pagada: { bg: '#d1fae5', color: '#059669' },
  parcial: { bg: '#fef3c7', color: '#d97706' },
  vencida: { bg: '#fee2e2', color: '#dc2626' },
  anulada: { bg: '#f1f5f9', color: '#94a3b8' },
};

export default function Facturas() {
  const esMovil = useEsMovil();
  const { params: paramsSede, nombre: nombreSede } = useSede();
  const { periodo, esActual, enLetras: periodoLargo } = usePeriodo();
  const navigate = useNavigate();
  const [facturas, setFacturas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [newDialog, setNewDialog] = useState(false);
  const [genDialog, setGenDialog] = useState(false);
  const [mensualDialog, setMensualDialog] = useState(false);
  const [mensualResultDialog, setMensualResultDialog] = useState(false);
  const [mensualResult, setMensualResult] = useState(null);
  const [newForm, setNewForm] = useState({ tercero_id: '', periodo_facturado: '', impuesto_pct: 19, moneda: 'USD' });
  const [genForm, setGenForm] = useState({ nara_empresa_id: '', periodo: '' });
  const [mensualForm, setMensualForm] = useState({
    periodo: new Date().toISOString().slice(0, 7), // "2026-02"
    impuesto_pct: 19
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [fRes, cRes] = await Promise.all([
        api.get('/facturas', { params: { estado: filtroEstado || undefined, tercero_id: filtroCliente || undefined, ...paramsSede } }),
        api.get('/terceros?rol=cliente'),
      ]);
      setFacturas(fRes.data);
      setClientes(cRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado, filtroCliente, paramsSede]);

  const handleCrear = async () => {
    setSaving(true);
    try {
      const res = await api.post('/facturas', newForm);
      setNewDialog(false);
      navigate(`/facturas/${res.data.id}`);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleGenerar = async () => {
    setSaving(true);
    try {
      const res = await api.post('/integracion/generar-desde-consumo', genForm);
      setGenDialog(false);
      navigate(`/facturas/${res.data.factura_id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al generar');
    }
    finally { setSaving(false); }
  };

  const handleGenerarMensual = async () => {
    setSaving(true);
    try {
      const res = await api.post('/integracion/generar-facturacion-mensual', mensualForm);
      setMensualDialog(false);
      setMensualResult(res.data);
      setMensualResultDialog(true);
      fetchData(); // Refrescar lista de facturas
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al generar facturacion mensual');
    }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>{facturas.length} facturas</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<FileDownloadIcon />}
            onClick={() => window.open(conEmpresa(`${api.defaults.baseURL}/facturas/exportar-excel`), '_blank')}
            sx={{ borderColor: '#059669', color: '#059669', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
            Excel
          </Button>
          <Button variant="contained" startIcon={<CalendarMonthIcon />} onClick={() => setMensualDialog(true)}
            color="success" disableElevation sx={{ borderRadius: 1 }}>
            Generar Facturacion del Mes
          </Button>
          <Button variant="outlined" startIcon={<AutoFixHighIcon />} onClick={() => setGenDialog(true)}
            sx={{ borderColor: '#1e3a5f', color: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
            Generar desde Consumo
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewDialog(true)}
            sx={{ bgcolor: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2d5a87' } }}>
            Nueva Factura
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField select label="Estado" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
          size="small" sx={filtro(140)}>
          <MenuItem value="">Todos</MenuItem>
          {['borrador', 'emitida', 'pagada', 'parcial', 'vencida', 'anulada'].map(e => (
            <MenuItem key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</MenuItem>
          ))}
        </TextField>
        <TextField select label="Cliente" value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}
          size="small" sx={filtro(200)}>
          <MenuItem value="">Todos</MenuItem>
          {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.razon_social}</MenuItem>)}
        </TextField>
      </Box>

      <Paper elevation={0} sx={CONTENEDOR_TABLA}>
        {facturas.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ReceiptIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>No hay facturas</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>Crea tu primera factura</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={tablaAncha(800)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>NUMERO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>CLIENTE</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>PERIODO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">TOTAL</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">PAGADO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">SALDO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>ESTADO</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facturas.map((f) => {
                  const ec = estadoColor[f.estado] || estadoColor.borrador;
                  return (
                    <TableRow key={f.id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                      onClick={() => navigate(`/facturas/${f.id}`)}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 13, color: '#1e3a5f' }}>{f.numero_factura}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: '#1e293b' }}>{f.tercero_nombre}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: '#64748b' }}>{f.periodo_facturado || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{pesos(f.total)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: '#10b981' }}>{pesos(f.total_pagado)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600, color: f.saldo_pendiente > 0 ? '#f59e0b' : '#10b981' }}>
                        {pesos(f.saldo_pendiente)}
                      </TableCell>
                      <TableCell>
                        <Chip label={f.estado} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: ec.bg, color: ec.color }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog Nueva Factura */}
      <Dialog fullScreen={esMovil} open={newDialog} onClose={() => setNewDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>Nueva Factura</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select label="Cliente *" value={newForm.tercero_id}
              onChange={(e) => setNewForm({ ...newForm, tercero_id: e.target.value })} size="small">
              {clientes.map(c => <MenuItem key={c.id} value={c.id}>{c.razon_social}</MenuItem>)}
            </TextField>
            {/* Viene del menú. Se deja a la vista porque fechar una factura
                tiene consecuencias: si el período activo no es el mes en curso
                hay que verlo, no descubrirlo en el cierre. */}
            <TextField label="Periodo" type="month" value={newForm.periodo_facturado || periodo}
              onChange={(e) => setNewForm({ ...newForm, periodo_facturado: e.target.value })}
              size="small" InputLabelProps={{ shrink: true }}
              helperText={esActual ? 'Del menú' : `⚠ ${periodoLargo} no es el mes actual`}
              FormHelperTextProps={{ sx: { color: esActual ? undefined : '#b45309' } }} />
            <TextField label="IVA %" value={newForm.impuesto_pct} type="number"
              onChange={(e) => setNewForm({ ...newForm, impuesto_pct: parseFloat(e.target.value) || 0 })} size="small" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewDialog(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleCrear} variant="contained" disabled={saving || !newForm.tercero_id}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#2d5a87' } }}>
            {saving ? 'Creando...' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Generar desde Consumo */}
      <Dialog fullScreen={esMovil} open={genDialog} onClose={() => setGenDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>Generar desde Consumo NARA</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: 13, mb: 2 }}>
            Consulta el consumo de servicios de NARA Chatbot y genera una factura automaticamente.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="NARA Empresa ID *" value={genForm.nara_empresa_id}
              onChange={(e) => setGenForm({ ...genForm, nara_empresa_id: e.target.value })} size="small"
              placeholder="ej: 4" helperText="ID de la empresa en NARA Chatbot" />
            <TextField label="Periodo *" type="month" InputLabelProps={{ shrink: true }}
              value={genForm.periodo || periodo}
              onChange={(e) => setGenForm({ ...genForm, periodo: e.target.value })} size="small"
              placeholder="ej: 2026-02" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGenDialog(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleGenerar} variant="contained" disabled={saving || !genForm.nara_empresa_id || !genForm.periodo}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#2d5a87' } }}>
            {saving ? 'Generando...' : 'Generar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Generar Facturacion Mensual */}
      <Dialog fullScreen={esMovil} open={mensualDialog} onClose={() => !saving && setMensualDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon sx={{ color: '#10b981' }} />
          Generar Facturacion Mensual
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
            Se generaran facturas para <strong>todas las empresas</strong> importadas desde NARA que tengan
            chatbots con planes pagos. Las empresas que ya tienen factura para este periodo seran omitidas.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Periodo *"
              type="month"
              value={mensualForm.periodo}
              onChange={(e) => setMensualForm({ ...mensualForm, periodo: e.target.value })}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="IVA %"
              value={mensualForm.impuesto_pct}
              type="number"
              onChange={(e) => setMensualForm({ ...mensualForm, impuesto_pct: parseFloat(e.target.value) || 0 })}
              size="small"
            />
          </Box>
          {saving && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ color: '#64748b', fontSize: 12, mb: 1 }}>Consultando empresas y generando facturas...</Typography>
              <LinearProgress sx={{ borderRadius: 1 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMensualDialog(false)} disabled={saving} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleGenerarMensual} variant="contained" disabled={saving || !mensualForm.periodo}
            color="success" disableElevation>
            {saving ? 'Generando...' : 'Generar Facturas'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Resultados Facturacion Mensual */}
      <Dialog fullScreen={esMovil} open={mensualResultDialog} onClose={() => setMensualResultDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>
          Resultado de Facturacion - {mensualResult?.periodo}
        </DialogTitle>
        <DialogContent>
          {mensualResult && (
            <Box>
              {/* Resumen KPIs */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#d1fae5', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#059669' }}>
                    {mensualResult.resumen.facturas_generadas}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#047857', fontWeight: 600 }}>GENERADAS</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#fef3c7', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#d97706' }}>
                    {mensualResult.resumen.empresas_omitidas}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#92400e', fontWeight: 600 }}>OMITIDAS</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: '#dbeafe', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#2563eb' }}>
                    {pesos(mensualResult.resumen.total_facturado)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#1d4ed8', fontWeight: 600 }}>TOTAL FACTURADO</Typography>
                </Box>
              </Box>

              {mensualResult.resumen.errores > 0 && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
                  {mensualResult.resumen.errores} empresa(s) con error al consultar NARA
                </Alert>
              )}

              {/* Detalle por empresa */}
              <Typography sx={{ fontWeight: 600, color: '#1e293b', mb: 1, fontSize: 14 }}>Detalle por empresa:</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {mensualResult.detalle.map((d, i) => (
                  <Box key={i} sx={{
                    p: 1.5, borderRadius: 1,
                    bgcolor: d.status === 'generada' ? '#f0fdf4' : d.status === 'error' ? '#fef2f2' : '#f8fafc',
                    border: `1px solid ${d.status === 'generada' ? '#bbf7d0' : d.status === 'error' ? '#fecaca' : '#e2e8f0'}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {d.status === 'generada' && <CheckCircleIcon sx={{ fontSize: 18, color: '#10b981' }} />}
                      {d.status === 'omitida' && <SkipNextIcon sx={{ fontSize: 18, color: '#f59e0b' }} />}
                      {d.status === 'error' && <ErrorOutlineIcon sx={{ fontSize: 18, color: '#ef4444' }} />}
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{d.empresa}</Typography>
                        {d.status === 'generada' && (
                          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                            {d.numero_factura} - {d.chatbots} chatbot(s) - {pesos(d.total)}
                          </Typography>
                        )}
                        {d.status !== 'generada' && (
                          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{d.razon}</Typography>
                        )}
                      </Box>
                    </Box>
                    {d.status === 'generada' && (
                      <Button size="small" onClick={() => { setMensualResultDialog(false); navigate(`/facturas/${d.factura_id}`); }}
                        sx={{ fontSize: 11, textTransform: 'none', color: '#1e3a5f', fontWeight: 600 }}>
                        Ver
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMensualResultDialog(false)} variant="contained"
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#2d5a87' }, textTransform: 'none', fontWeight: 600 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
